<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    direction="right"
    width="600px"
    :title="isEditing ? `Edit ${casedDisplayName}` : `Add ${casedDisplayName}`"
  >
    <AddNewDynamicEntityForm
      v-if="internalDrawerVisible && hasAccess"
      :properties="properties"
      :sub-objects="subObjects"
      :slug="slug"
      :service="service"
      :visible="internalDrawerVisible"
      :initial-data="initialData"
      :entity-id="entityId"
      :entity-display-name="entityDisplayName"
      :bulk-create-mode="bulkCreateMode"
      :hide-parent-field="hideParentField"
      @form-ready="handleFormReady"
      @form-submit="handleFormSubmit"
      @properties-empty="handlePropertiesEmpty"
      @duplicate-found="handleDuplicateFound"
      ref="formRef"
    />

    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button class="secondary_medium" @click="onCancel">Cancel</a-button>
        <a-button
          class="primary_medium"
          @click="onSave"
          :loading="saving"
          :disabled="hasNoProperties || hasDuplicateRecords"
        >
          {{
            isEditing
              ? saving
                ? "Updating..."
                : `Update ${casedDisplayName}`
              : saving
                ? `Adding...`
                : `Add ${casedDisplayName}`
          }}
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { capitalizeFirstLetter,getCasedDisplayName } from "@/src/utils/utils.js";
import { message } from "ant-design-vue";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import AddNewDynamicEntityForm from "@/src/views/dynamic_crm/AddNewDynamicEntityForm.vue";
import { triggerEvents } from "@/src/constants/formConstants.js";
import { useFormTrigger } from "@/src/composables/forms/useFormTrigger.js";
import CRMService from "@/src/api/CRMService.js";
import {
  transformFormDataForAPI,
  normalizeOriginalDataForTransform,
  patchStandardEntity,
  createStandardEntity,
} from "@/src/utils/dynamicFormUtils.js";
import { STANDARD_OBJECT_SLUGS } from "@/src/constants/crmConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { extractDetailedErrorMessage } from "@/src/utils/errorUtils.js";
import { getEntityDetailRoute, shouldSkipDetailRedirect } from "@/src/utils/entityRouteUtils.js";

const props = defineProps({
  visible: { type: Boolean, required: true },
  slug: { type: String, required: true },
  service: { type: String, default: null },
  entityDisplayName: { type: String, required: true },
  properties: { type: Array, default: () => [] },
  subObjects: { type: Array, default: () => [] },
  entityId: { type: [String, Number], default: null },
  initialData: { type: Object, default: () => ({}) },
  skipApiCall: { type: Boolean, default: false },
  objectId: { type: String, default: null },
  bulkCreateMode: { type: Boolean, default: false },
  bulkCreateData: { type: Object, default: () => ({}) },
  skipNavigation: { type: Boolean, default: false },
  hideParentField: { type: Boolean, default: false },
});

const emit = defineEmits(["update:visible", "entity-saved", "cancel"]);

const store = useStore();
const { executeBeforeTrigger, executeAfterTrigger, resetEntityFormTriggers } = useFormTrigger();

const internalDrawerVisible = ref(props.visible);
const saving = ref(false);
const formRef = ref(null);
const formMethods = ref(null);
const hasNoProperties = ref(false);
const hasDuplicateRecords = ref(false);
const router = useRouter();

const isEditing = computed(() => !!props.entityId);
const initialData = computed(() => ({ ...props.initialData }));

const isStandardEntity = computed(() => {
  return STANDARD_OBJECT_SLUGS.includes(props.slug);
});

const isHrObject = computed(() => {
  const schema = store.getters["schema/getSchemaBySlug"]?.(props.slug);
  return schema?.object?.service === "hr";
});

const hasCreateAccess = computed(() => {
  if (isHrObject.value) return true;
  if (store.getters["rbac/isABACMode"]) {
    return store.getters["rbac/canPerformAction"](props.slug, "CREATE");
  }
  return true;
});

const hasEditAccess = computed(() => {
  if (isHrObject.value) return true;
  if (store.getters["rbac/isABACMode"]) {
    return store.getters["rbac/canPerformAction"](props.slug, "EDIT");
  }
  return true;
});

const hasAccess = computed(() => {
  return isEditing.value ? hasEditAccess.value : hasCreateAccess.value;
});


const casedDisplayName = computed(() => {
  return getCasedDisplayName(props.entityDisplayName);
});

const handleFormReady = (methods) => {
  formMethods.value = methods;
};

const handleFormSubmit = async (formData) => {
  await saveEntity(formData);
};

const handlePropertiesEmpty = (isEmpty) => {
  hasNoProperties.value = isEmpty;
};

const handleDuplicateFound = ({ hasDuplicate }) => {
  hasDuplicateRecords.value = hasDuplicate;
};

const onSave = async () => {
  if (formMethods.value) {
    // Check for duplicates before saving
    if (formMethods.value.hasDuplicates?.()) {
      message.error("Please resolve duplicate record conflicts before saving");
      return;
    }

    try {
      await formMethods.value.validateForm();
      const formData = formMethods.value.getFormData();
      await saveEntity(formData);
    } catch (error) {
      console.log("Form validation failed");
    }
  }
};

const saveEntity = async (formData) => {
  saving.value = true;

  try {
    // If skipApiCall is true, just emit the event without calling API
    if (props.skipApiCall) {
      saving.value = false;
      emit("entity-saved", {
        data: formData,
        isEdit: false,
      });
      onCancel();
      return;
    }

    const baseline = isEditing.value
      ? formMethods.value?.getOriginalBaseline?.()
      : null;
    // Use filteredProperties from form (includes virtual relationship properties)
    const propertiesForTransform =
      formMethods.value?.getFilteredProperties?.() || props.properties;
    const transformedData = transformFormDataForAPI(
      formData,
      propertiesForTransform,
      baseline,
      { allowExplicitFalse: true },
    );
    // Add sub_object_type_id to attributes if it exists in formData
    if (formData.sub_object_type_id) {
      transformedData.attributes.sub_object_type_id =
        formData.sub_object_type_id;
    }

    const payload = {
      attributes: transformedData.attributes,
      relations: transformedData.relations,
    };

    if (props.slug === entityTypes.DEALS && formData.currency_code) {
      payload.attributes.currency_code = formData.currency_code;
    }

    if ([entityTypes.CONTACTS, entityTypes.LEADS, entityTypes.COMPANIES].includes(props.slug) && formData.list_ids) {
      const currentListIds = formData.list_ids || [];
      const originalListIds = baseline?.list_ids || [];
      if (JSON.stringify(currentListIds) !== JSON.stringify(originalListIds)) {
        payload.attributes.list_ids = currentListIds;
      }
    }

    if ("parent_id" in formData) {
      payload.parent_id = formData.parent_id;
    }

    if ("suite_id" in formData && formData.suite_id) {
      payload.attributes.suite_id = formData.suite_id;
    }

    if ("test_id" in formData && formData.test_id) {
      payload.attributes.test_id = formData.test_id;
    }

    let response;

    if (isEditing.value) {
      const updatedAttributes = Object.keys(payload.attributes);
      const { proceed } = await executeBeforeTrigger(
        props.slug,
        props.entityId,
        triggerEvents.ENTITY_UPDATED,
        updatedAttributes,
      );
      if (!proceed) {
        saving.value = false;
        return;
      }

      // Use patchStandardEntity for standard objects, updateDynamicEntity for custom objects
      const wrappedPayload = { object: { attributes: payload.attributes, relations: payload.relations } };
      response = isStandardEntity.value
        ? await patchStandardEntity(CRMService, props.slug, props.entityId, wrappedPayload, props.service)
        : await CRMService.updateDynamicEntity(props.slug, props.entityId, payload, props.service);

      if (response?.success) {
        message.success(`${props.entityDisplayName} updated successfully`);
        saving.value = false;
        emit("update:visible", false);

        await executeAfterTrigger(props.entityId);

        // If tags were updated, refresh schema so new tag options appear everywhere
        const tagsAttr = (props.properties || []).find((p) => p.id === "tags");
        const tagsSlug = tagsAttr?.slug || "tags";
        if (payload.attributes[tagsSlug] !== undefined) {
          store.dispatch("schema/refreshSchema", {
            slug: props.slug,
            service: props.service || null,
          });
        }

        emit("entity-saved", {
          data: response.data,
          isEdit: true,
        });
      } else {
        message.error(extractDetailedErrorMessage(response, `Failed to update ${props.entityDisplayName.toLowerCase()}`));
        saving.value = false;
      }
    } else {
      if (props.bulkCreateMode) {
        const bulkPayload = {
          target_object_type: props.bulkCreateData.target_object_type,
          relationship_id: props.bulkCreateData.relationship_id,
          selected_ids: props.bulkCreateData.selected_ids || [],
          unselected_ids: props.bulkCreateData.unselected_ids || [],
          select_all: props.bulkCreateData.select_all || false,
          view_id: props.bulkCreateData.view_id,
          list_id: props.bulkCreateData.list_id,
          search_query: props.bulkCreateData.search_query,
          attributes: transformedData.attributes,
        };
        response = await CRMService.bulkCreateForEntities(
          props.slug,
          bulkPayload,
          props.service,
        );

        if (response?.success) {
          message.success(
            response.message ||
              `${props.entityDisplayName}s creation queued successfully`,
          );
          saving.value = false;
          emit("entity-saved", {
            data: response.data,
            isEdit: false,
            isBulk: true,
          });
          onCancel();
        } else {
          message.error(
            `Failed to create ${props.entityDisplayName.toLowerCase()}`
          );
          saving.value = false;
          if (newId && !props.skipNavigation && !shouldSkipDetailRedirect(props.slug)) {
            const subObjectTypeId = response?.record?.sub_object_type_id;
            const detailPath = getEntityDetailRoute({ entityType: props.slug, recordId: newId, service: props.service }) || `/crm/${props.slug}/${newId}`;
            const routeConfig = subObjectTypeId
              ? { path: detailPath, query: { sub_object: subObjectTypeId } }
              : { path: detailPath };

            router.push(routeConfig);
          }
          emit("entity-saved", {
            data: response.data,
            isEdit: wasEditing,
          });
        }
      } else {
        // Use createStandardEntity for standard objects, createDynamicEntity for custom objects
        response = isStandardEntity.value
          ? await createStandardEntity(CRMService, props.slug, payload, [], props.service)
          : await CRMService.createDynamicEntity(props.slug, payload, props.service);
        if (response?.success) {
          const newId = response?.record?.id || null;

          message.success(`${props.entityDisplayName} created successfully`);
          saving.value = false;
          emit("update:visible", false);

          await executeAfterTrigger(newId);

          if (newId && !props.skipNavigation && !shouldSkipDetailRedirect(props.slug)) {
            const subObjectTypeId = response?.record?.sub_object_type_id;
            const detailPath = getEntityDetailRoute({ entityType: props.slug, recordId: newId, service: props.service }) || `/crm/${props.slug}/${newId}`;
            const routeConfig = subObjectTypeId
              ? { path: detailPath, query: { sub_object: subObjectTypeId } }
              : { path: detailPath };

            router.push(routeConfig);
          }
          // If tags were included, refresh schema so new tag options appear everywhere
          const tagsAttr = (props.properties || []).find((p) => p.id === "tags");
          const tagsSlug = tagsAttr?.slug || "tags";
          if (payload.attributes[tagsSlug] !== undefined) {
            store.dispatch("schema/refreshSchema", {
              slug: props.slug,
              service: props.service || null,
            });
          }

          emit("entity-saved", {
            data: response.data,
            isEdit: false,
          });
        } else {
          message.error(extractDetailedErrorMessage(response, `Failed to create ${props.entityDisplayName.toLowerCase()}`));
          saving.value = false;
        }
      }
    }
  } catch (error) {
    console.error("Error saving entity:", error);
    message.error(extractDetailedErrorMessage(error, `An error occurred while ${isEditing.value ? "updating" : "creating"} the ${props.entityDisplayName.toLowerCase()}`));
    saving.value = false;
  }
};

const onCancel = () => {
  resetEntityFormTriggers();
  emit("update:visible", false);
  emit("cancel");
};

const resetForm = () => {
  if (formMethods.value) {
    formMethods.value.resetForm();
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    if (newVal) {
      if (isEditing.value) {
        internalDrawerVisible.value = true;
      } else {
        const { proceed, cancelled } = await executeBeforeTrigger(
          props.slug,
          null,
          triggerEvents.ENTITY_CREATED,
        );

        if (!proceed || cancelled) {
          emit("update:visible", false);
          return;
        }

        internalDrawerVisible.value = true;
      }
    } else {
      internalDrawerVisible.value = false;
    }
  },
  { immediate: true },
);

watch(
  () => internalDrawerVisible.value,
  (newVal) => {
    if (!newVal) {
      emit("update:visible", false);
    }
  },
);
</script>
